import { fromJS } from 'immutable';
import flatten from 'lodash/flatten';

import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import {
  updateChunksFilterById,
  updateCustomGroupsDataById,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';

import { byValue, ascending, descending, occurrence, AFFILIATIONS_FILTER_ID } from '@Containers/ResultsFilters/ResultsFiltersConstants';

import {
  paramsForFilterGroups,
  keyifyParamsForFiltersRequest,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';

import { handleFilterWithChunks, getSortingParams } from '@Containers/ResultsFilters/performSearchFilterAsync/preparePropertiesUtils';

import { performSearchFilterAsync } from '@Containers/ResultsFilters/performSearchFilterAsync';

import { getAllGroupsFromChunks } from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersUtils';

import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { isNewAuthorSearchFilterEnabled } from '@Containers/ResultsFilters/utils';
import { setSelectedItemsFromDialog } from '@Containers/ResultsFilters/ResultsFiltersComponents/SelectedItemsFromDialog/SelectedItemsFromDialogActions';
import { getNomalizedGroupsAsChunksAsync } from './BarChartCheckboxesDialogServis';
import {
  sizeChunckOfGroups,
  sizeChunckOfGroupsNewAuthorSearchFilter,
  typeOfBarChartCheckboxesDialog,
} from './BarChartCheckboxesDialogConstants';

// eslint-disable-next-line no-shadow
export function open(filterId, keyifyParamsForFiltersRequest) {
  return updateDialog({
    id: keyifyParamsForFiltersRequest + filterId,
    movable: false,
    filterId,
    dialogType: typeOfBarChartCheckboxesDialog,
    backdrop: true,
    sortingType: occurrence,
    sorting: descending,
    activeChunkIndex: 0,
    keyifyParamsForFiltersRequest,
  });
}

export function setChunksSorting(dialogId, sorting) {
  return updateDialog({
    sorting,
    activeChunkIndex: 0,
    id: dialogId,
    backdrop: true,
    dialogType: typeOfBarChartCheckboxesDialog,
  });
}

export function setChunksSortingType(dialogId, sortingType) {
  const sorting = sortingType === byValue ? ascending : descending;

  return updateDialog({
    sorting,
    activeChunkIndex: 0,
    sortingType,
    id: dialogId,
    backdrop: true,
    dialogType: typeOfBarChartCheckboxesDialog,
  });
}

export function setActiveChunkId(dialogId, activeChunkIndex) {
  return updateDialog({
    activeChunkIndex: parseInt(activeChunkIndex, 10),
    id: dialogId,
    backdrop: true,
    dialogType: typeOfBarChartCheckboxesDialog,
  });
}

export function syncResult(result, filter, isSortingChanged, isSortingTypeChanged) {
  let nonChunk = filter.has('clearGroupValues') && filter.get('clearGroupValues') ? filter.get('clearGroupValues') : filter.get('groups');

  if (isSortingChanged || isSortingTypeChanged) {
    nonChunk = filter.get('groups');
  }

  const groups = nonChunk.filter((item) => item.get('selected'));
  const groupsFromChunks = getAllGroupsFromChunks(filter);
  const { groupCount, chunksCount, chunks } = result;

  return {
    groupCount,
    chunksCount,
    chunks: chunks.map((item) => {
      const { id } = item;
      let selected = false;
      // eslint-disable-next-line no-shadow
      const itemFromGroup = groups.find((item) => item.get('id') === id);
      const itemFromChunks = groupsFromChunks.get(id);
      const resultGroup = itemFromChunks || itemFromGroup;

      if (!resultGroup) {
        return item;
      }
      if ((itemFromGroup && itemFromGroup.get('selected')) || (itemFromChunks && itemFromChunks.get('selected'))) {
        selected = true;
      }

      return {
        ...item,
        selected: selected || resultGroup.get('selected'),
      };
    }),
  };
}

// eslint-disable-next-line max-lines-per-function
export function getGroupsAsChunks(
  filter,
  chunkIndex,
  sortingType,
  sorting,
  byLetter,
  customGroupsWithByValueSorting,
  isSortingChanged,
  isSortingTypeChanged
) {
  return async (dispatch, getState) => {
    const state = getState();
    const filterId = filter.get('id');
    const chunkSize = isNewAuthorSearchFilterEnabled(filterId) ? sizeChunckOfGroupsNewAuthorSearchFilter : sizeChunckOfGroups;
    const activeChunkIdAsInteger = parseInt(chunkIndex, 10);
    const key = keyifyParamsForFiltersRequest(state);
    const first = chunkSize * activeChunkIdAsInteger + 1;
    const last = first + chunkSize - 1;
    const propertiesObject = {
      property: filterId,
      all: true,
      ...getSortingParams(sortingType, sorting),
      ...(byLetter && { byLetter }),
    };

    if (customGroupsWithByValueSorting) {
      propertiesObject.all = true;
    } else {
      propertiesObject.all = false;
      propertiesObject.first = first;
      propertiesObject.last = last;
    }

    const properties = [propertiesObject];
    const baseParams = paramsForFilterGroups(state);
    const resultParams = {
      ...baseParams,
      properties,
    };
    const result = syncResult(await getNomalizedGroupsAsChunksAsync(resultParams), filter, isSortingChanged, isSortingTypeChanged);

    dispatch(
      updateChunksFilterById(
        key,
        filterId,
        sortingType,
        sorting,
        result,
        chunkIndex.toString(),
        byLetter,
        customGroupsWithByValueSorting,
        isSortingChanged,
        isSortingTypeChanged
      )
    );
  };
}

export function setActiveCustomGroupIndex(dialogId, activeCustomGroupIndex) {
  return updateDialog({
    activeCustomGroupIndex,
    id: dialogId,
    backdrop: true,
    dialogType: typeOfBarChartCheckboxesDialog,
  });
}

export function getCustomGroupsData(filter, sortingType, sorting) {
  return async (dispatch, getState) => {
    const state = getState();
    const filterId = filter.get('id');
    const key = keyifyParamsForFiltersRequest(state);
    const properties = [
      {
        property: filterId,
        all: false,
        ...getSortingParams(sortingType, sorting),
      },
    ];
    const baseParams = paramsForFilterGroups(state);
    const resultParams = {
      ...baseParams,
      properties,
    };

    const data = syncResult(await getNomalizedGroupsAsChunksAsync(resultParams), filter);

    dispatch(updateCustomGroupsDataById(key, filterId, data));
  };
}

export const limitTo = withGlobalSpinnerActionEnhancer((filter, sortingType) => async (dispatch) => {
  try {
    const { properties, selectedItems } = handleFilterWithChunks(filter, sortingType);

    dispatch(setSelectedItemsFromDialog(filter, flatten(selectedItems)));

    await performSearchFilterAsync({
      filters: fromJS([filter]),
      properties,
    });
  } catch (e) {
    Logger.error(e);
  }
});

export const exclude = withGlobalSpinnerActionEnhancer((filter, sortingType) => async (dispatch) => {
  try {
    const { properties, selectedItems } = handleFilterWithChunks(filter, sortingType);

    dispatch(setSelectedItemsFromDialog(filter, flatten(selectedItems)));

    await performSearchFilterAsync({
      filters: fromJS([filter]),
      properties,
      baseParamsExtension: { exclude: true },
    });
  } catch (e) {
    Logger.error(e);
  }
});

export function openAffiliationFilter() {
  return (dispatch, getState) => {
    const filterId = AFFILIATIONS_FILTER_ID;
    const key = keyifyParamsForFiltersRequest(getState());
    dispatch(open(filterId, key));
  };
}
